import { AGENT_URL } from '@/config/env'
import {
  buildListSearchParams,
  fetchJsonWithAuth,
  isRecord,
  parsePagination,
} from '@/lib/api'
import type { ListQueryParams, Pagination } from '@/lib/api'
import type {
  ClaimDetail,
  ClaimHeader,
  ClaimLine,
  ClaimsPage,
} from '@/features/claims/types'

export const CLAIMS_PAGE_LIMIT = 50

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/
const DECIMAL = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/

function invalid(scope: string, field: string): never {
  throw new Error(`Invalid ${scope}: invalid ${field}`)
}

function requiredId(
  row: Record<string, unknown>,
  field: string,
  scope: string
): string {
  const value = row[field]
  if (typeof value !== 'string' || value.trim() === '') {
    return invalid(scope, field)
  }
  return value
}

function optionalString(
  row: Record<string, unknown>,
  field: string,
  scope: string
): string | null {
  const value = row[field]
  if (value == null) return null
  if (typeof value !== 'string') return invalid(scope, field)
  return value
}

function optionalDate(
  row: Record<string, unknown>,
  field: string,
  scope: string
): string | null {
  const value = optionalString(row, field, scope)
  if (value === null) return null

  const parts = ISO_DATE.exec(value)
  if (!parts) return invalid(scope, field)
  const year = Number(parts[1])
  const month = Number(parts[2])
  const day = Number(parts[3])
  const date = new Date(Date.UTC(year, month - 1, day))
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return invalid(scope, field)
  }
  return value
}

function optionalDecimal(
  row: Record<string, unknown>,
  field: string,
  scope: string
): string | null {
  const value = optionalString(row, field, scope)
  if (value !== null && !DECIMAL.test(value)) return invalid(scope, field)
  return value
}

function claimHeaderFields(
  row: Record<string, unknown>,
  scope: string
): ClaimHeader {
  return {
    id: requiredId(row, 'id', scope),
    source_claim_id: requiredId(row, 'source_claim_id', scope),
    patient_id: optionalString(row, 'patient_id', scope),
    patient_name: optionalString(row, 'patient_name', scope),
    source_patient_id: optionalString(row, 'source_patient_id', scope),
    service_date: optionalDate(row, 'service_date', scope),
    status: optionalString(row, 'status', scope),
    claim_amount: optionalDecimal(row, 'claim_amount', scope),
    paid_amount: optionalDecimal(row, 'paid_amount', scope),
    provider_name: optionalString(row, 'provider_name', scope),
    provider_npi: optionalString(row, 'provider_npi', scope),
  }
}

export function mapClaimHeader(value: unknown): ClaimHeader {
  if (!isRecord(value)) invalid('claims response', 'row')
  return claimHeaderFields(value, 'claims response')
}

function mapClaimLine(value: unknown): ClaimLine {
  if (!isRecord(value)) invalid('claim detail response', 'line')
  return {
    id: requiredId(value, 'id', 'claim detail response'),
    source_line_id: requiredId(
      value,
      'source_line_id',
      'claim detail response'
    ),
    service_date: optionalDate(value, 'service_date', 'claim detail response'),
    status: optionalString(value, 'status', 'claim detail response'),
    revenue_code: optionalString(
      value,
      'revenue_code',
      'claim detail response'
    ),
    service_code: optionalString(
      value,
      'service_code',
      'claim detail response'
    ),
    units: optionalDecimal(value, 'units', 'claim detail response'),
    claim_amount: optionalDecimal(
      value,
      'claim_amount',
      'claim detail response'
    ),
    paid_amount: optionalDecimal(value, 'paid_amount', 'claim detail response'),
  }
}

export function mapClaimDetail(value: unknown): ClaimDetail {
  if (!isRecord(value) || !Array.isArray(value.lines)) {
    invalid('claim detail response', 'shape')
  }
  return {
    ...claimHeaderFields(value, 'claim detail response'),
    lines: value.lines.map(mapClaimLine),
  }
}

function mapPagination(value: unknown): Pagination {
  if (!isRecord(value)) invalid('claims pagination', 'shape')
  const { limit, has_more: hasMore, next_cursor: nextCursor, total } = value
  if (
    typeof limit !== 'number' ||
    !Number.isInteger(limit) ||
    limit < 1 ||
    limit > 200
  ) {
    invalid('claims pagination', 'limit')
  }
  if (typeof hasMore !== 'boolean') invalid('claims pagination', 'has_more')
  if (nextCursor != null && typeof nextCursor !== 'string') {
    invalid('claims pagination', 'next_cursor')
  }
  if (hasMore && (typeof nextCursor !== 'string' || nextCursor === '')) {
    invalid('claims pagination', 'next_cursor')
  }
  if (
    total != null &&
    (typeof total !== 'number' || !Number.isInteger(total) || total < 0)
  ) {
    invalid('claims pagination', 'total')
  }

  return parsePagination(
    {
      limit,
      has_more: hasMore,
      next_cursor: nextCursor ?? null,
      total: total ?? null,
    },
    CLAIMS_PAGE_LIMIT
  )
}

export async function fetchClaimsPage({
  token,
  params,
  cursor,
  signal,
}: {
  token: string
  params: ListQueryParams
  cursor?: string
  signal?: AbortSignal
}): Promise<ClaimsPage> {
  const search = buildListSearchParams(params, cursor)
  const response = await fetchJsonWithAuth<unknown>({
    token,
    url: `${AGENT_URL}/data/claims?${search.toString()}`,
    signal,
    errorMessage: 'Failed to fetch claims',
  })
  if (!isRecord(response) || !Array.isArray(response.data)) {
    invalid('claims response', 'shape')
  }

  return {
    data: response.data.map(mapClaimHeader),
    pagination: mapPagination(response.pagination),
  }
}

export async function fetchClaimDetail(
  token: string,
  claimId: string,
  signal?: AbortSignal
): Promise<ClaimDetail> {
  const response = await fetchJsonWithAuth<unknown>({
    token,
    url: `${AGENT_URL}/data/claims/${encodeURIComponent(claimId)}`,
    signal,
    errorMessage: 'Failed to fetch claim detail',
  })
  return mapClaimDetail(response)
}
